import Button from "@/common/components/Button";
import { CopyButton } from "@/common/components/CopyButton";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import ResumeItem from "@/common/components/ResumeItem";
import TextArea from "@/common/components/TextArea";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities } from "@/common/constants/unities.constant";
import { useCopyButton } from "@/common/hooks/useCopyButton";
import { formatDecimal, formatUnit } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { MaintenanceForwardingCheckinEntryData } from "@/features/privateCompanies/pages/Maintenance/Workflows/Show/features/forwardings/types/maintenanceForwarding.type";
import { useMaintenanceTransportAutoEntriesComplete } from "@/features/privateCompanies/pages/Maintenance/Workflows/Show/hooks/forwardings/transport/autoEntries/useMaintenanceTransportAutoEntriesComplete";
import { transportAutoEntriesCompleteSchema, TransportAutoEntriesCompleteValidationSchema } from "@/features/privateCompanies/pages/Maintenance/Workflows/Show/schemas/forwardings/transport/autoEntries/transportAutoEntriesComplete.schema";
import { useWorkflowShowStore } from "@/features/privateCompanies/pages/Maintenance/Workflows/Show/stores/workflowShowStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { SignpostIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

interface TransportAutoEntriesDeliveryCodeWithCompleteProps {
  checkinEntry: MaintenanceForwardingCheckinEntryData;
}

const TransportAutoEntriesDeliveryCodeWithComplete = ({
  checkinEntry,
}: TransportAutoEntriesDeliveryCodeWithCompleteProps) => {
  const { id } = useParams();

  const [deliveryCodeModalIsOpen, setDeliveryCodeModalIsOpen] = useState(false);
  const [completeModalIsOpen, setCompleteModalIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    equipment,
    currentEquipmentUsage,
  } = useWorkflowShowStore();

  const {
    control: controlCopyDeliveryCode,
    copySuccess: copyDeliveryCodeSuccess,
  } = useCopyButton();

  const {
    register,
    control,
    reset,
    setError,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm<TransportAutoEntriesCompleteValidationSchema>({
    resolver: zodResolver(transportAutoEntriesCompleteSchema),
    defaultValues: {
      current_equipment_usage: equipment?.last_usage_value
        ? formatUnit(
          formatDecimal(equipment?.last_usage_value, 0),
          equipment?.model?.category?.unit_for_consumption || 'kilometers'
        )
        : undefined,
      observations: ''
    },
  });

  const {
    mutate: transportAutoEntriesComplete,
    isPending: transportAutoEntriesCompleteIsPending,
  } = useMaintenanceTransportAutoEntriesComplete({
    checkinEntryId: checkinEntry.id,
    deliveryCode: checkinEntry.delivery_code,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', id] });
        queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflowForwardings', id] });
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding'] });

        setCompleteModalIsOpen(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    },
  });

  const onSubmit: SubmitHandler<TransportAutoEntriesCompleteValidationSchema> = (data) => transportAutoEntriesComplete(data);

  const handleCopyDeliveryCode = () => {
    copyDeliveryCodeSuccess(checkinEntry.delivery_code);
  }

  useEffect(() => {
    reset({
      current_equipment_usage: equipment?.last_usage_value
        ? formatUnit(
          formatDecimal(equipment?.last_usage_value, 0),
          equipment?.model?.category?.unit_for_consumption || 'kilometers'
        )
        : undefined,
      observations: '',
    });
  }, [currentEquipmentUsage]);

  return (
    <>
      <Modal
        isOpen={deliveryCodeModalIsOpen}
        setIsOpen={setDeliveryCodeModalIsOpen}
        Trigger={(
          <Button>
            <SignpostIcon size={20} />

            Realizar Check-in
          </Button>
        )}
        title="Código de Check-In/Out"
        titleClass="text-2xl"
        bodyClassName="text-white-800"
        body={(
          <div className="flex items-center justify-between">
            <ResumeItem
              label="Código para Check-In/Out"
              value={checkinEntry.delivery_code ? (
                <div className="flex items-center gap-1.5">
                  <p>
                    {checkinEntry.delivery_code}
                  </p>

                  <CopyButton
                    control={controlCopyDeliveryCode}
                    onClick={handleCopyDeliveryCode}
                    className="text-blue-800 size-4"
                  />
                </div>
              ) : '--'}
            />

            <Button
              variant="neutral"
              operationKlassToCheckPermission="Admin::Maintenance::Transport::AutoEntries::Complete"
              onClick={() => {
                setDeliveryCodeModalIsOpen(false);
                setCompleteModalIsOpen(true);
              }}
            >
              Ignorar etapa
            </Button>
          </div>
        )}
      />

      <Modal
        isOpen={completeModalIsOpen}
        setIsOpen={setCompleteModalIsOpen}
        title="Coleta do equipamento"
        titleClass="text-2xl"
        bodyClassName="text-white-800"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={transportAutoEntriesCompleteIsPending}
        body={(
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <Controller
                control={control}
                name="current_equipment_usage"
                render={({ field }) => (
                  <UnitTextField
                    label="Odômetro/Horímetro"
                    markAsRequired
                    decimals={0}
                    unit={unities[equipment?.model?.category?.unit_for_consumption || 'kilometers']}
                    valueClassName="h-full flex-1"
                    {...field}
                    errorMessage={errors.current_equipment_usage?.message}
                  />
                )}
              />

              <TextArea
                label="Observações de check-in"
                rows={3}
                {...register('observations')}
                errorMessage={errors.observations?.message}
              />
            </div>
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default TransportAutoEntriesDeliveryCodeWithComplete;
